// use-add-team-member.ts — Teams suite, action
//
// POST /api/teams/:teamId/members. Adds a member to a team.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AddTeamMemberResponse, CreateTeamMemberRequest } from '@cabal/shared/domain/session/team/member/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { teamsDetailKey, teamsRootKey } from './team-query-keys';

export interface AddTeamMemberVars {
  teamId: string;
  member: CreateTeamMemberRequest;
}

export function useAddTeamMember(): ActionResult<AddTeamMemberVars, AddTeamMemberResponse> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<AddTeamMemberVars, AddTeamMemberResponse>(
    useMemo(
      () => ({
        mutationFn: ({ teamId, member }) =>
          api.teams.byId(teamId).members.add({ body: member }),
        onInvalidate: (_data, { teamId }) => {
          queryClient.invalidateQueries({ queryKey: teamsDetailKey(teamId) });
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
